import React from 'react';
import { useTranslation } from 'react-i18next';

function formatValue(value) {
  const raw = String(value || '').trim();
  return raw || '-';
}

function ProfilePartnershipModal({
  isOpen,
  account,
  searchQuery,
  selectedPartner,
  searchResults,
  isSearching,
  searchError,
  isSaving,
  saveError,
  onClose,
  onSearchQueryChange,
  onSelectPartner,
  onSave,
  onUnlink,
}) {
  const { t } = useTranslation();

  if (!isOpen || !account) {
    return null;
  }

  return (
    <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="partnership-modal-title">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title" id="partnership-modal-title">
                {t('profiles.partnerAssignment')}
              </h5>
              <div className="text-muted small">{formatValue(account.login)}</div>
            </div>
            <button type="button" className="btn-close" aria-label={t('common.close')} onClick={onClose} disabled={isSaving} />
          </div>

          <div className="modal-body">
            <div className="card card-body mb-3">
              <h6 className="mb-3">{t('profiles.accountSummary')}</h6>
              <div className="row g-3 small">
                <div className="col-sm-6 col-lg-3">
                  <div className="text-muted text-uppercase mb-1">{t('profiles.login')}</div>
                  <div>{formatValue(account.login)}</div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="text-muted text-uppercase mb-1">{t('common.type')}</div>
                  <div>{formatValue(account.type)}</div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="text-muted text-uppercase mb-1">{t('profiles.platform')}</div>
                  <div>{formatValue(account.platform)}</div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="text-muted text-uppercase mb-1">{t('profiles.currency')}</div>
                  <div>{formatValue(account.currency)}</div>
                </div>
                <div className="col-12">
                  <div className="text-muted text-uppercase mb-1">{t('profiles.currentPartner')}</div>
                  <div>
                    {account.currentPartner ? (
                      account.currentPartner.display || `${formatValue(account.currentPartner.name)} <${formatValue(account.currentPartner.email)}>`
                    ) : (
                      <span className="badge rounded-pill fw-semibold text-uppercase app-badge-disabled">
                        {t('profiles.unlinked')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="position-relative mb-3">
              <label className="form-label">{t('profiles.partnerSearchLabel')}</label>
              <input
                type="text"
                className="form-control"
                value={searchQuery}
                onChange={(event) => onSearchQueryChange(event.target.value)}
                placeholder={t('profiles.partnerSearchPlaceholder')}
                disabled={isSaving}
                autoComplete="off"
              />

              {isSearching ? <div className="text-muted small mt-2">{t('profiles.searchingPartners')}</div> : null}
              {searchError ? <div className="alert alert-danger mt-2 mb-0">{searchError}</div> : null}
              {saveError ? <div className="alert alert-danger mt-2 mb-0">{saveError}</div> : null}

              {!isSearching && !searchError && searchQuery.trim().length >= 2 && searchResults.length === 0 ? (
                <div className="text-muted small mt-2">{t('profiles.noPartnerResults')}</div>
              ) : null}

              {searchResults.length > 0 ? (
                <div className="list-group position-absolute w-100 shadow-sm mt-1" style={{ zIndex: 5 }}>
                  {searchResults.map((partner) => (
                    <button
                      key={partner.uid || partner.display}
                      type="button"
                      className={`list-group-item list-group-item-action ${selectedPartner?.uid === partner.uid ? 'active' : ''}`}
                      onClick={() => onSelectPartner(partner)}
                      disabled={isSaving}
                    >
                      <div className="fw-semibold">{partner.display}</div>
                      <div className="small opacity-75">{partner.uid}</div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {selectedPartner ? (
              <div className="alert alert-info mb-0">
                <div className="text-uppercase small fw-semibold mb-1">{t('profiles.selectedPartner')}</div>
                <div>{selectedPartner.display}</div>
              </div>
            ) : null}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={isSaving}>
              {t('common.close')}
            </button>
            <button type="button" className="btn btn-outline-danger" onClick={onUnlink} disabled={isSaving}>
              {t('profiles.unlinkPartner')}
            </button>
            <button type="button" className="btn btn-primary" onClick={onSave} disabled={isSaving || !selectedPartner}>
              {isSaving ? t('metrics.saving') : t('profiles.savePartner')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePartnershipModal;
